import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { PageFrame as UiPageFrame } from "@/components/ui/PageFrame";
import { BackLink, type BackLinkProps } from "./BackLink";
import { ErrorState } from "./ErrorState";
import { LoadingState } from "./LoadingState";

export interface PageFrameProps {
  title?: string;
  description?: string;
  icon?: LucideIcon;
  actions?: ReactNode;
  /** Custom breadcrumb slot; overrides `backLink` when set. */
  breadcrumb?: ReactNode;
  /** Standard back navigation rendered above the title (same slot as `breadcrumb`). */
  backLink?: BackLinkProps;
  className?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  error?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
  children?: ReactNode;
}

/**
 * App page chrome: `ui/PageFrame` plus loading/error states and `BackLink` with router defaults.
 */
export function PageFrame({
  title,
  description,
  icon,
  actions,
  breadcrumb,
  backLink,
  className,
  isLoading = false,
  loadingMessage,
  error = false,
  errorMessage,
  onRetry,
  isRetrying = false,
  children,
}: PageFrameProps) {
  const resolvedBreadcrumb =
    breadcrumb ?? (backLink ? <BackLink {...backLink} /> : undefined);
  const showActions = actions && !isLoading && !error;

  return (
    <UiPageFrame
      title={title}
      description={description}
      icon={icon}
      actions={showActions ? actions : undefined}
      breadcrumb={resolvedBreadcrumb}
      className={className}
    >
      {isLoading ? (
        <LoadingState message={loadingMessage} />
      ) : error ? (
        <ErrorState
          message={errorMessage}
          onRetry={onRetry}
          isRetrying={isRetrying}
        />
      ) : (
        children
      )}
    </UiPageFrame>
  );
}
